import type RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { CART, CartView } from '../cart/CartModel';
import { damp } from '../util/format';
import { sampleSpline, type SplineFrame } from '../tracks/spline';
import type { ColliderMeta } from '../tracks/types';

const MIN_PLAYER_GAP = 2.2;

export interface Opponent {
  plate: string;
  meters: number;
  speed: number;
  baseSpeed: number;
  lane: number;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  view: CartView;
}

export class OpponentField {
  readonly racers: Opponent[] = [];

  constructor(
    rapier: typeof RAPIER,
    private readonly world: RAPIER.World,
    private readonly meta: Map<number, ColliderMeta>,
    views: CartView[],
    frames: SplineFrame[],
    lapLength: number,
  ) {
    const specs = [
      { plate: '2', meters: 22, baseSpeed: 11.2, lane: -2.6 },
      { plate: '3', meters: 36, baseSpeed: 12.1, lane: 2.6 },
      { plate: '4', meters: 50, baseSpeed: 10.6, lane: 1.5 },
    ];
    specs.forEach((spec, index) => {
      const view = views[index];
      const sample = sampleSpline(frames, spec.meters, lapLength);
      const position = sample.position.clone().addScaledVector(sample.normal, CART.rideHeight).addScaledVector(sample.binormal, spec.lane);
      const body = world.createRigidBody(
        rapier.RigidBodyDesc.kinematicPositionBased().setTranslation(position.x, position.y, position.z),
      );
      body.setNextKinematicRotation({
        x: sample.quaternion.x,
        y: sample.quaternion.y,
        z: sample.quaternion.z,
        w: sample.quaternion.w,
      });
      const collider = world.createCollider(
        rapier.ColliderDesc.cuboid(CART.collider.hx, CART.collider.hy, CART.collider.hz)
          .setTranslation(0, CART.collider.y, CART.collider.z)
          .setSensor(true),
        body,
      );
      this.meta.set(collider.handle, { kind: 'vehicle', surface: 'asphalt' });
      view.setPose(position, sample.quaternion);
      this.racers.push({
        plate: spec.plate,
        meters: spec.meters,
        speed: 0,
        baseSpeed: spec.baseSpeed,
        lane: spec.lane,
        body,
        collider,
        view,
      });
    });
  }

  update(
    dt: number,
    frames: SplineFrame[],
    lapLength: number,
    playerMeters: number,
    racing: boolean,
    playerPosition?: { x: number; z: number },
  ): void {
    for (const racer of this.racers) {
      const sample = sampleSpline(frames, racer.meters, lapLength);
      const corner = Math.min(1, Math.abs(frames[Math.floor((racer.meters / lapLength) * frames.length) % frames.length]?.bank ?? 0) / 0.12);
      let target = racer.baseSpeed * (1 - corner * 0.28);
      const gap = playerMeters - racer.meters;
      target *= 1 + THREE.MathUtils.clamp(gap / 140, -0.12, 0.18);
      if (!racing) target = 0;
      racer.speed = damp(racer.speed, target, dt, 1.6);
      racer.meters += racer.speed * dt;
      const pose = sampleSpline(frames, racer.meters, lapLength);
      const position = pose.position.clone().addScaledVector(pose.normal, CART.rideHeight).addScaledVector(pose.binormal, racer.lane);
      if (playerPosition) separateFromPlayer(position, playerPosition, pose.binormal);
      racer.body.setNextKinematicTranslation({ x: position.x, y: position.y, z: position.z });
      racer.body.setNextKinematicRotation({ x: pose.quaternion.x, y: pose.quaternion.y, z: pose.quaternion.z, w: pose.quaternion.w });
      racer.view.setPose(position, pose.quaternion);
      const steer = THREE.MathUtils.clamp((pose.tangent.x - sample.tangent.x) * 4, -0.45, 0.45);
      racer.view.update(dt, steer, racer.speed, 0, false, false);
    }
  }

  dispose(): void {
    for (const racer of this.racers) {
      this.meta.delete(racer.collider.handle);
      this.world.removeRigidBody(racer.body);
      racer.view.root.removeFromParent();
      racer.view.dispose();
    }
    this.racers.length = 0;
  }
}

function separateFromPlayer(position: THREE.Vector3, player: { x: number; z: number }, binormal: THREE.Vector3): void {
  let dx = position.x - player.x;
  let dz = position.z - player.z;
  let dist = Math.hypot(dx, dz);
  if (dist >= MIN_PLAYER_GAP) return;
  if (dist < 0.05) {
    dx = binormal.x;
    dz = binormal.z;
    dist = Math.hypot(dx, dz) || 1;
  }
  const scale = MIN_PLAYER_GAP / dist;
  position.x = player.x + dx * scale;
  position.z = player.z + dz * scale;
}
